import axios from "axios";
import { getAuthTokenFromLS, getOrCreateDeviceId, getOrCreateBrowserSessionId } from "./common";
import { BASE_URL, IS_TUNNELLED_API } from "./constant";
import { persistor, store } from "../redux/store";
import { clearCredentials } from "../redux/reducer";

// The store is the source of truth once the app is running; localStorage covers
// the first request after a reload, and the sessionStorage temp token covers the
// half-finished two-factor login.
const currentToken = () => {
  try {
    return store.getState()?.app?.token || getAuthTokenFromLS() || sessionStorage.getItem("jwttoken") || "";
  } catch (e) {
    return getAuthTokenFromLS() || "";
  }
};

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 100000,
});

axiosInstance.interceptors.request.use(
  (config) => {

    const token = currentToken();
    // Sending `Authorization: ""` is not the same as sending nothing: Express
    // reads an empty header as absent and the API answers "Authorization header
    // not found", which reads like a broken client rather than "no session
    // yet". Omit the header instead.
    if (token) {
      config.headers["Authorization"] = token;
    } else {
      delete config.headers["Authorization"];
      // Most endpoints on this instance require a session, so a token-less call
      // is almost always a missing guard in the caller. The stack names it.
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          `[auth] ${config.method?.toUpperCase()} ${config.url} is about to call the API with no session`,
          { stack: new Error("unauthenticated request").stack }
        );
      }
    }
    config.headers["x-device-id"] = getOrCreateDeviceId();
    config.headers["x-session-id"] = getOrCreateBrowserSessionId();
    // See utils/constant.js — without this an ngrok-tunnelled API never gets
    // the request at all; the browser only reports a CORS error.
    if (IS_TUNNELLED_API) config.headers["ngrok-skip-browser-warning"] = "true";

    return config;
  },
  (error) => {

    return Promise.reject(error);
  }
);

const forceLogout = (redirectTo) => {
  // Nothing to log out of. Without this, an anonymous 401 (a request that went
  // out before a session existed) tore down the store and storage underneath
  // the mounted tree, which then re-issued its queries unauthenticated.
  if (!currentToken()) return;
  store.dispatch(clearCredentials());
  store.dispatch({ type: "USER_LOGOUT" });
  persistor.purge();
  // Only navigate when we are not already there. Reloading /login from a 401
  // that fired on /login would restart the same request and loop. The redux
  // store and storage have already been purged above, so staying put is clean.
  if (window.location.pathname !== redirectTo) {
    window.location.replace(redirectTo);
  }
};

axiosInstance.interceptors.response.use(
  (config) => {

    // Some auth failures still arrive as HTTP 200 carrying a FAILURE envelope, so
    // detect them from the payload rather than relying on the status code alone.
    if (config?.data?.result?.status === "UNAUTHORIZED") {
      forceLogout("/login");
      return Promise.reject(new Error(config?.data?.message || "Unauthorized"));
    }

    if (config?.data?.message === "Authorization Invalid" || config?.data?.message === "invalid token") {
      forceLogout("/login");
      return Promise.reject(new Error(config?.data?.message));
    }
    return config;
  },
  (error) => {
    // A transport failure is NOT an auth failure. `!error.response` covers
    // timeouts, offline, cancelled requests and CORS errors, so treating it as a
    // logout meant any network blip signed the user out. Let the caller handle it.
    if (error.response && error.response.status === 401) {
      // Clear local storage and redirect to the login page
      forceLogout("/login");
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);
export default axiosInstance;